import { Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';
import { Login } from './pages/Login';
import { Tariffs } from './pages/Tariffs';
import { Reset } from './pages/Reset';
import { Signup } from './pages/Signup';
import { Dashboard } from './pages/Dashboard';
import { useEffect, useState } from 'react';
import { PageLoading } from './components/PageLoading';
import { useGetUserQuery } from './features/api/apiSlice';
import { Broadcast } from './pages/Broadcast';
import { Profile } from './pages/Profile';
import Error from './pages/Error';
import Referral from './pages/Referral/Referral';
import BroadcastsMobile from './pages/BroadcastsMobile';
import Widget from './pages/Widget';
import PaymentHistory from './pages/PaymentHistory';
import { getRouteParams } from './helpers/getRouteParams';
import { useAppDispatch, useAppSelector } from './app/hooks';
import NotifyContainer from './components/Notify/NotifyContainer';
import { addNotify } from './features/notifySlice';
import { useReferralToken } from './hooks/useReferralToken';
import { useUpdateRefTokenMutation } from './features/api/apiSlice';
import { useLazyGetNotificationsQuery } from './features/api/apiSlice';
import { useUtmTag } from './hooks/useUtmTag';
import { useUpdateUtmMutation } from './features/api/apiSlice';
import { SocketProvider } from './providers/SocketProvider/SocketProvider';
import { Studio } from './pages/Studio';
import StreamLayoutPage from './pages/StreamLayoutPage/StreamLayoutPage';
import { parseStudioParams } from './helpers/parseStudioParams';
import useNoTranslate from './hooks/useNoTranslate';

const AUTH_EXEMPT_ROUTES = ["/widget","/stream-layout"];

function App() {
    useNoTranslate();
    const [ getNotifications ] = useLazyGetNotificationsQuery();
    const { tokenFromStorage, removeTokenFromStorage } = useReferralToken();
    const { utmTagFromStorage, removeUtmTagFromStorage } = useUtmTag();
    const [updateToken] = useUpdateRefTokenMutation();
    const [updateUtm] = useUpdateUtmMutation();

    const [loginServerError, setLoginServerError] = useState(''); // Нужно чтобы уменьшить время появления спиннера после ошибки авторизации
    const location = useLocation();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const { theme } = useAppSelector((state) => state.options);

    const [searchParams] = useSearchParams();
    const layoutParam = searchParams.get('layout');
    const parsedLayout = parseStudioParams(layoutParam);
    const widgetParams = getRouteParams(window.location.href);
    const isWidgetPage = location.pathname === "/widget";

    const {
        isFetching: isUserFetching,
        isSuccess: isUserSuccess,
        data: user,
    } = useGetUserQuery(widgetParams.chat_token);

    // * Theme state
    useEffect(() => {
        const app = document.querySelector("#root") as HTMLElement;

        if (theme === "dark") {
            app.classList.remove("light");
            app.classList.add(theme);
            return;
        };

        app.classList.remove("dark");
        app.classList.add(theme);
    }, [theme]);

    // * Отправляет реферальный токен на сервер если он есть в localstorage
    // * используется для пользователей соц. сетей
    useEffect(() => {
        if (!tokenFromStorage || !user) return;

        updateToken({
            uid: user?.user_id,
            ref_token: tokenFromStorage
        });
        removeTokenFromStorage();
    }, [user]);

    useEffect(() => {
        if (!user) return;

        getNotifications({ isUnread: true })
            .unwrap()
            .then((data) => {
                if (data.notifications.length === 0) return;

                data.notifications.forEach((notify) => {
                    dispatch(addNotify(notify));
                });
            });
    }, [user]);

    useEffect(() => {
        if (utmTagFromStorage && user) {
            updateUtm({ userId: user.user_id, utmTag: utmTagFromStorage });
            removeUtmTagFromStorage();
        }
    }, [user]);

    if (isUserFetching) {
        return <PageLoading />;
    }

    const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/reset';

    if (!isUserSuccess && !isAuthPage && !AUTH_EXEMPT_ROUTES.includes(location.pathname)) {
        navigate('/login', { replace: true });
    } else if (isUserSuccess && isAuthPage && !AUTH_EXEMPT_ROUTES.includes(location.pathname)) {
        navigate('/', { replace: true });
    }

    return (
        <>
            <NotifyContainer />
            <SocketProvider
                userId={isUserSuccess ? user?.user_id : undefined}
                chatToken={widgetParams.chat_token}
                broadcastId={isWidgetPage ? Number(widgetParams.id) : undefined}
                studioToken={parsedLayout?.studio_token}
            >
                <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/broadcasts" element={<BroadcastsMobile />} />
                    <Route path="/login" element={<Login serverError={loginServerError} setServerError={setLoginServerError} />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/reset" element={<Reset />} />
                    <Route path="/tariffs" element={<Tariffs />} />
                    <Route path="/broadcast/:id" element={<Broadcast />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/payment-history" element={<PaymentHistory />} />
                    <Route path="/widget" element={<Widget />} />
                    <Route path="/referral" element={<Referral />} />
                    <Route path="/studio" element={<Studio />} />
                    <Route path="/stream-layout" element={<StreamLayoutPage />} />


                    <Route path="*" element={<Error message="Not Found" description='Простите, страница не найдена' />} />
                </Routes>
            </SocketProvider>
        </>
    );
}

export default App;
